import { Box, Heading, Text, VStack, SimpleGrid, Badge, Button, List, ListItem, ListIcon, IconButton, Table, Thead, Tr, Th, Tbody, Td, Link, useToast } from '@chakra-ui/react';
import { FaCheckCircle, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { pricingService, adminService } from '../services/firestore';
import { SubscriptionPlan, AdminSettings } from '../types/user';
import { useAuthContext } from '../contexts/AuthContext';
import { AnalyticsService } from '../services/analytics';

export default function Pricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { isAuthenticated } = useAuthContext();
  const [pricingPlans, setPricingPlans] = useState<SubscriptionPlan[]>([]);
  const [adminSettings, setAdminSettings] = useState<AdminSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [exchangeRate, setExchangeRate] = useState<string>(() => {
    return localStorage.getItem('exchangeRate') || '18.50'
  });

  // Check if we came from registration
  const fromRegister = searchParams.get('from') === 'register';
  const preSelectedPlan = searchParams.get('plan');

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load pricing plans and admin settings in parallel
        const [plans, settings] = await Promise.all([
          pricingService.getPricingPlans(),
          adminService.getAdminSettings()
        ]);
        
        setPricingPlans(plans);
        setAdminSettings(settings);
        
        // Set exchange rate from admin settings if available
        if (settings?.exchangeRate) {
          setExchangeRate(settings.exchangeRate);
        }
        
        // Track pricing page view
        await AnalyticsService.logEvent('pricing_page_viewed', {
          is_authenticated: isAuthenticated,
          from_register: fromRegister,
          pre_selected_plan: preSelectedPlan || null
        });
      } catch (error) {
        console.error('Error loading data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load pricing information',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [toast, isAuthenticated, fromRegister, preSelectedPlan]);

  // Group plans by type for display
  const nonDiscountedPlans = pricingPlans.filter(plan => !('discountPercentage' in plan) || plan.discountPercentage === 0 || plan.discountPercentage === undefined);
  const individualPlans = nonDiscountedPlans.filter(plan => plan.type === 'individual');
  const clubPlans = nonDiscountedPlans.filter(plan => plan.type === 'club');

  // Default club pricing tiers (fallback if not in database)
  const defaultClubPricing = {
    1: 39000, // R390.00 per court
    2: 37000, // R370.00 per court
    3: 35000, // R350.00 per court
    4: 33000, // R330.00 per court
    5: 31000, // R310.00 per court
  };

  // Handle plan selection
  const handlePlanSelection = async (plan: SubscriptionPlan) => {
    // Set loading state for authenticated users (upgrades)
    if (isAuthenticated) {
      setIsUpgrading(true);
    }
    
    try {
      // Track plan selection
      await AnalyticsService.logEvent('plan_selected', {
        plan_type: plan.type,
        plan_name: plan.name,
        amount: plan.price,
        currency: 'ZAR',
        is_authenticated: isAuthenticated
      });
      
      // Store plan details in localStorage (as in Phase 6)
      localStorage.setItem('registrationPlanId', plan.id || '');
      localStorage.setItem('registrationPlanName', plan.name || '');
      localStorage.setItem('registrationOriginalPlanName', plan.name || ''); // Store original plan name
      
      // Store the total amount (per-court price × number of courts for club plans)
      const totalAmount = plan.type === 'club' && plan.courts 
        ? plan.price * plan.courts 
        : plan.price
      localStorage.setItem('registrationOriginalAmount', totalAmount.toString());
      
      // Clear any existing promo code (will be applied later if user verifies card)
      localStorage.removeItem('registrationPromoCode');
      localStorage.removeItem('registrationPromoCodeId');
      localStorage.removeItem('registrationDiscountPercentage');
      
      const params = new URLSearchParams();
      params.set('plan', plan.type);
      if (plan.type === 'club' && plan.courts) {
        params.set('courts', plan.courts.toString());
      }
      
      if (isAuthenticated) {
        // Logged-in users go directly to payment page
        navigate(`/paywall?${params.toString()}`);
      } else {
        // Non-logged-in users go to registration
        navigate(`/register?${params.toString()}`);
      }
    } catch (error) {
      console.error('Error in plan selection:', error);
      toast({
        title: 'Error',
        description: 'Failed to select plan. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      // Reset loading state for authenticated users
      if (isAuthenticated) {
        setIsUpgrading(false);
      }
    }
  };

  // Get trial duration text
  const getTrialText = () => {
    const trialDays = adminSettings?.trialDurationDays ?? 30;
    
    if (trialDays === 1) {
      return '1 day FREE trial on all packages!';
    } else if (trialDays === 30) {
      return '1 month FREE trial on all packages!';
    } else if (trialDays === 60) {
      return '2 months FREE trial on all packages!';
    } else if (trialDays === 90) {
      return '3 months FREE trial on all packages!';
    } else {
      return `${trialDays} days FREE trial on all packages!`;
    }
  };

  return (
    <Box minH="100vh" bg="#09090b" color="white" py={16} px={4}>
      {/* Back Arrow */}
      <Box position="absolute" top={4} left="50%" transform="translateX(-50%)" zIndex={9999}>
        <IconButton
          aria-label="Back to landing page"
          icon={<FaArrowLeft />}
          onClick={() => navigate('/')}
          variant="ghost"
          color="white"
          _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
          size="lg"
        />
      </Box>
      <VStack spacing={8} maxW="4xl" mx="auto">
        <Heading as="h1" size="2xl" fontWeight="bold" textAlign="center">
          Pricing
        </Heading>
        <VStack spacing={1} maxW="2xl">
          <Text fontSize="xl" textAlign="center">
            <Text as="span" color="#e2e700" fontStyle="italic">1 month FREE trial</Text>
            <Text as="span" color="gray.300"> on all packages!</Text>
          </Text>
          <Text fontSize="lg" color="gray.400" textAlign="center">
            No card details needed. Cancel anytime.
          </Text>
        </VStack>
        {fromRegister && (
          <Box bg="blue.900" p={4} borderRadius="lg" border="1px solid" borderColor="blue.400" maxW="2xl">
            <Text fontSize="md" color="blue.200" textAlign="center">
              Select a plan to continue with your registration.
            </Text>
          </Box>
        )}
        
        {isLoading ? (
          <Text color="gray.400">Loading pricing information...</Text>
        ) : (
          <>
            {/* Exchange Rate Display */}
            <Text fontSize="md" color="gray.300" textAlign="center" mb={6}>
              1 USD = ZAR {exchangeRate} <span style={{fontSize: '0.8rem', color: '#666'}}>approximately</span>
            </Text>
            
            {/* All Pricing Plans */}
            {nonDiscountedPlans.length > 0 && (
              <VStack spacing={6} w="full" maxW="4xl" mx="auto">
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} w="full">
                  {nonDiscountedPlans.map((plan, index) => (
                    <Box key={plan.id || index} bg="#191919" borderRadius="lg" p={8} border={plan.type === 'club' ? "2px solid" : "2px solid"} borderColor={plan.type === 'club' ? "#3182ce" : "#38A169"} textAlign="center" boxShadow={plan.type === 'club' ? "lg" : "lg"} display="flex" flexDirection="column" justifyContent="space-between">
                      <Badge colorScheme={plan.type === 'club' ? 'blue' : 'green'} mb={4} fontSize="xl" px={4} py={2}>{plan.name?.toUpperCase() || (plan.type === 'club' ? 'CLUB PLAN' : 'INDIVIDUAL PLAN')}</Badge>
                      <Text fontSize="2xl" fontWeight="bold" mb={4}>
                        ZAR {(plan.price / 100).toLocaleString()}<span style={{fontSize: '0.8rem', color: '#aaa'}}> {plan.type === 'club' ? '/court per month' : '/month'}</span>
                      </Text>
                      <List spacing={3} color="gray.300" mb={6} flex="1" textAlign="left" style={{ listStyle: 'none', padding: 0 }}>
                        {plan.features?.map((feature: string, featureIndex: number) => (
                          <ListItem key={featureIndex} display="flex" alignItems="flex-start" gap={2}>
                            <ListIcon as={FaCheckCircle} color={plan.type === 'club' ? 'blue.400' : 'green.400'} flexShrink={0} mt={1} />
                            <Text flex="1">{feature}</Text>
                          </ListItem>
                        ))}
                      </List>
                      {plan.type === 'club' && plan.courts && plan.courts > 1 && (
                        <Text fontSize="sm" color="gray.400" mb={3}>
                          Total: ZAR {((plan.price / 100) * plan.courts).toLocaleString()}/month
                        </Text>
                      )}
                      <Button 
                        colorScheme={plan.type === 'club' ? 'blue' : 'green'} 
                        variant="solid" 
                        w="full" 
                        onClick={() => handlePlanSelection(plan)}
                      >
                        {isAuthenticated ? 'Upgrade to this plan' : 'Select This Plan'}
                      </Button>
                    </Box>
                  ))}
                </SimpleGrid>
              </VStack>
            )}

            {/* No plans available */}
            {nonDiscountedPlans.length === 0 && (
              <VStack spacing={6} w="full" maxW="2xl" mx="auto" pt={8}>
                <Box bg="#191919" borderRadius="lg" p={8} border="1px solid" borderColor="gray.700" textAlign="center">
                  <Heading as="h2" size="lg" color="gray.400" mb={4}>
                    No Pricing Plans Available
                  </Heading>
                  <Text color="gray.300" mb={6}>
                    Pricing plans are currently being configured. Please check back later or contact us for more information.
                  </Text>
                  <Button 
                    colorScheme="blue" 
                    onClick={() => navigate('/')}
                    leftIcon={<FaArrowLeft />}
                  >
                    Back to Home
                  </Button>
                </Box>
              </VStack>
            )}
          </>
        )}
      </VStack>


    </Box>
  );
} 